import React from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
  Row,
  Col,
  Card,
  CardBody,
} from 'reactstrap';
import { Content } from '../../types';
import HlsVideoPlayer from '../HlsVideoPlayer';
import PortraitMediaFrame from '../PortraitMediaFrame';
import ThumbnailPreviewFrame from '../ThumbnailPreviewFrame';
import { formatUkDateTime } from '../../utils/dateFormat';

interface ContentDetailModalProps {
  content: Content | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: () => void;
}

const ContentDetailModal: React.FC<ContentDetailModalProps> = ({
  content,
  isOpen,
  onClose,
  onEdit,
}) => {
  if (!content) return null;

  const getStatusBadge = (status: string) => {
    const colors: { [key: string]: string } = {
      published: 'success',
      draft: 'warning',
      archived: 'secondary',
    };
    return <Badge color={colors[status] || 'secondary'}>{status}</Badge>;
  };

  const formatDuration = (seconds: number) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" className="content-detail-modal">
      <ModalHeader toggle={onClose}>
        <div className="d-flex align-items-center gap-2">
          <i className="ri-file-music-line fs-4"></i>
          Content Details
        </div>
      </ModalHeader>
      <ModalBody>
        <Row>
          {/* Thumbnail */}
          {content.thumbnail && (
            <Col md={12} className="mb-4 d-flex justify-content-center">
              <ThumbnailPreviewFrame
                src={content.thumbnail}
                alt={content.title}
                maxWidth={360}
                maxHeight={240}
              />
            </Col>
          )}

          {/* Title and Status */}
          <Col md={12} className="mb-3">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <h4 className="mb-2">{content.title}</h4>
                {content.shortDesc && (
                  <p className="text-muted mb-2">{content.shortDesc}</p>
                )}
              </div>
              <div className="d-flex gap-2 flex-wrap">
                {content.modules?.length ? (
                  <Badge color="secondary">
                    <i className="ri-stack-line me-1"></i>Module ({content.modules.length} days)
                  </Badge>
                ) : (
                  <Badge color="light" className="text-dark border">Single</Badge>
                )}
                {getStatusBadge(content.status || 'draft')}
              </div>
            </div>
          </Col>

          {/* Audio/Video Player */}
          {content.fileUrl && (
            <Col md={12} className="mb-4">
              <div className="media-player rounded overflow-hidden border">
                {content.type === 'audio' ? (
                  <div className="p-3 bg-light">
                    <div className="d-flex align-items-center mb-3">
                      <i className="ri-music-2-line fs-2 text-primary me-3"></i>
                      <div>
                        <h6 className="mb-0">Audio Player</h6>
                        <small className="text-muted">Duration: {formatDuration(content.duration || 0)}</small>
                      </div>
                    </div>
                    <audio controls className="w-100" controlsList="nodownload">
                      <source src={content.fileUrl} type="audio/mpeg" />
                      <source src={content.fileUrl} type="audio/mp4" />
                      <source src={content.fileUrl} type="audio/wav" />
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                ) : content.type === 'video' ? (
                  <div className="d-flex justify-content-center py-2">
                    <PortraitMediaFrame poster={content.thumbnail} size={260}>
                      <HlsVideoPlayer
                        src={content.fileUrl}
                        poster={content.thumbnail}
                        controls
                        fit="contain"
                        className="w-100 h-100"
                        controlsList="nodownload"
                      />
                    </PortraitMediaFrame>
                  </div>
                ) : (
                  <div className="p-3 bg-light text-center">
                    <i className="ri-file-line fs-1 text-muted"></i>
                    <p className="mb-0 mt-2">Media file available</p>
                    <a href={content.fileUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary mt-2">
                      <i className="ri-download-line me-1"></i>
                      Open File
                    </a>
                  </div>
                )}
              </div>
            </Col>
          )}

          {/* Description */}
          {content.description && (
            <Col md={12} className="mb-3">
              <h6 className="fw-semibold mb-2">
                <i className="ri-file-text-line me-2"></i>
                Description
              </h6>
              <p className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>
                {content.description}
              </p>
            </Col>
          )}

          {/* Transcript */}
          {content.transcript && !content.modules?.length && (
            <Col md={12} className="mb-3">
              <h6 className="fw-semibold mb-2">
                <i className="ri-chat-quote-line me-2"></i>
                Transcript
              </h6>
              <div className="p-3 bg-light rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                <p className="mb-0 small" style={{ whiteSpace: 'pre-wrap' }}>
                  {content.transcript}
                </p>
              </div>
            </Col>
          )}

          {/* Introduction videos (module/journey content) */}
          {(() => {
            const introList =
              content.introductions && content.introductions.length > 0
                ? content.introductions
                : content.introduction?.fileUrl
                  ? [content.introduction]
                  : [];
            if (!introList.length) return null;
            return (
              <Col md={12} className="mb-3">
                <h6 className="fw-semibold mb-3">
                  <i className="ri-video-line me-2"></i>
                  Introduction Videos ({introList.length})
                </h6>
                <div className="d-flex flex-column gap-2">
                  {introList.map((intro, idx) => (
                    <Card key={idx} className="border">
                      <CardBody className="py-3">
                        <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                          <i className="ri-video-line me-2 text-primary fs-5"></i>
                          <strong>{intro.title || `Introduction ${idx + 1}`}</strong>
                          {intro.duration ? (
                            <Badge color="info">{formatDuration(intro.duration)}</Badge>
                          ) : null}
                        </div>
                        {intro.fileUrl && (
                          <div className="mb-2 d-flex justify-content-center">
                            <PortraitMediaFrame poster={intro.thumbnail} size={200}>
                              <HlsVideoPlayer
                                src={intro.fileUrl}
                                poster={intro.thumbnail}
                                controls
                                fit="contain"
                                className="w-100 h-100"
                              />
                            </PortraitMediaFrame>
                          </div>
                        )}
                        {intro.thumbnail && (
                          <div className="mb-2 d-flex justify-content-center">
                            <ThumbnailPreviewFrame src={intro.thumbnail} alt={intro.title || 'Introduction thumbnail'} maxWidth={220} />
                          </div>
                        )}
                        {intro.transcript && (
                          <div className="p-2 bg-light rounded small" style={{ maxHeight: '120px', overflowY: 'auto' }}>
                            {intro.transcript}
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  ))}
                </div>
              </Col>
            );
          })()}

          {/* Modules */}
          {content.modules && content.modules.length > 0 && (
            <Col md={12} className="mb-3">
              <h6 className="fw-semibold mb-3">
                <i className="ri-stack-line me-2"></i>
                Modules ({content.modules.length})
              </h6>
              <div className="d-flex flex-column gap-2">
                {content.modules.map((mod: any, idx: number) => (
                  <Card key={idx} className="border">
                    <CardBody className="py-3">
                      <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                        <i className="ri-calendar-check-line me-2 text-primary fs-5"></i>
                        <strong>Day {mod.day}: {mod.title}</strong>
                        {mod.duration ? (
                          <Badge color="info">{formatDuration(mod.duration)}</Badge>
                        ) : null}
                      </div>
                      {mod.shortDesc && <p className="text-muted small mb-2">{mod.shortDesc}</p>}
                      {mod.description && <p className="mb-2 small" style={{ whiteSpace: 'pre-wrap' }}>{mod.description}</p>}
                      {mod.fileUrl && (
                        <div className="mb-2 d-flex justify-content-center">
                          {mod.type === 'audio' ? (
                            <audio controls className="w-100" style={{ maxWidth: 360 }}>
                              <source src={mod.fileUrl} />
                            </audio>
                          ) : mod.type === 'video' ? (
                            <PortraitMediaFrame poster={mod.thumbnail} size={200}>
                              <HlsVideoPlayer
                                src={mod.fileUrl}
                                poster={mod.thumbnail}
                                controls
                                fit="contain"
                                className="w-100 h-100"
                              />
                            </PortraitMediaFrame>
                          ) : (
                            <a href={mod.fileUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                              <i className="ri-download-line me-1"></i>Open File
                            </a>
                          )}
                        </div>
                      )}
                      {mod.transcript && (
                        <div className="p-2 bg-light rounded small mb-2" style={{ maxHeight: '120px', overflowY: 'auto' }}>
                          {mod.transcript}
                        </div>
                      )}
                      {mod.reflection && (mod.reflection.title || mod.reflection.description || mod.reflection.bulletPoints?.length) && (
                        <div className="mt-2 pt-2 border-top">
                          <small className="text-muted fw-semibold d-block mb-1">
                            <i className="ri-lightbulb-line me-1"></i>
                            Reflection
                          </small>
                          {mod.reflection.title && <p className="mb-1 fw-semibold small">{mod.reflection.title}</p>}
                          {mod.reflection.description && (
                            <p className="mb-2 small text-muted" style={{ whiteSpace: 'pre-wrap' }}>
                              {mod.reflection.description}
                            </p>
                          )}
                          {mod.reflection.bulletPoints && mod.reflection.bulletPoints.length > 0 && (
                            <ul className="mb-0 ps-3 small">
                              {mod.reflection.bulletPoints.map((point: string, bi: number) => (
                                <li key={bi}>{point}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                      {mod.questions && mod.questions.length > 0 && (
                        <div className="mt-2 pt-2 border-top">
                          <small className="text-muted fw-semibold d-block mb-1">
                            <i className="ri-chat-quote-line me-1"></i>
                            Questions ({mod.questions.length})
                          </small>
                          <ul className="mb-0 ps-3 small">
                            {mod.questions.map((q: any, qi: number) => (
                              <li key={qi}>
                                <strong>Q{q.order || qi + 1}:</strong> {q.prompt}
                                {q.instruction && <span className="text-muted"> — {q.instruction}</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                ))}
              </div>
            </Col>
          )}

          {/* Metadata */}
          <Col md={12}>
            <div className="border-top pt-3">
              <Row className="g-3">
                <Col sm={6}>
                  <small className="text-muted d-block">Created</small>
                  <span>{formatUkDateTime(content.createdAt)}</span>
                </Col>
                <Col sm={6}>
                  <small className="text-muted d-block">Last Updated</small>
                  <span>{formatUkDateTime(content.updatedAt)}</span>
                </Col>
                {content.duration && (
                  <Col sm={6}>
                    <small className="text-muted d-block">Duration</small>
                    <span>{formatDuration(content.duration)}</span>
                  </Col>
                )}
                {content.fileUrl && (
                  <Col sm={6}>
                    <small className="text-muted d-block">File URL</small>
                    <a 
                      href={content.fileUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary text-decoration-none"
                    >
                      <i className="ri-external-link-line me-1"></i>
                      Open in new tab
                    </a>
                  </Col>
                )}
              </Row>
            </div>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose}>
          <i className="ri-close-line me-1"></i>
          Close
        </Button>
        {onEdit && (
          <Button color="primary" onClick={onEdit}>
            <i className="ri-edit-line me-1"></i>
            Edit Content
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default ContentDetailModal;
